"use client";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import NavLayout from "@/components/library/NavLayout";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {useUserOrganizations} from "@/contexts/common/UserOrganizationsProvider";
import {GailProductRoles} from "@/enums/user";
import {useOrg} from "@/hooks/useOrg";
import {GlobalNavSectionType} from "@/types/common/library";
import {ClearAllCache} from "@/utils/common/cache";
import {useRouter} from "@bprogress/next";
import BrandImage from "../BrandImage";

export type BaseNavContainerProps = {
   children: React.ReactNode;
   menuSections: GlobalNavSectionType[];
   sideNavMessage?: string;
   onUserManagementClick?: () => void;
};

export default function BaseNavContainer({
   children,
   menuSections,
   sideNavMessage,
   onUserManagementClick,
}: Readonly<BaseNavContainerProps>) {
   const {t} = useTranslation("common");
   const {user, logout} = useAuthContext();
   const {orgs} = useUserOrganizations();
   const {ui, productRolesState} = useOrg();
   const {isDockAvailable, isOpen: isDockOpen} = useGailDock();
   const router = useRouter();
   const brand = ui.brand;

   const handleLogoClick = () => {
      if (productRolesState.roles?.includes(GailProductRoles.GailGPTUser)) {
         router.push("/gailgpt");
      } else {
         router.push("/");
      }
   };

   const handleLogout = () => {
      ClearAllCache();
      logout();
   };

   return (
      <>
         {!!user && (
            <NavLayout
               logo={
                  <button
                     type="button"
                     onClick={handleLogoClick}
                     className={clsx("flex", "items-center", "h-full")}
                     aria-label={t("notFound.goHome")}
                  >
                     <BrandImage brand={brand} width={65} />
                  </button>
               }
               minimizedLogo={
                  <div className={clsx("flex", "items-center", "h-full")}>
                     <BrandImage brand={brand} width={23} height={23} variant="small" />
                  </div>
               }
               menuSections={menuSections}
               user={user}
               userActions={{
                  onLogoutClick: handleLogout,
                  onUserManagementClick: orgs.length > 1 ? onUserManagementClick : undefined,
               }}
               sideNavMessage={sideNavMessage}
               userHasAlert={false}
               // While the assistant has the stage, the app chrome recedes;
               // hover restores it. The assistant entry itself stays loud.
               isNavDimmed={isDockAvailable && isDockOpen}
            >
               {children}
            </NavLayout>
         )}
         {!user && <>{children}</>}
      </>
   );
}
